import React, { ReactNode, useEffect } from "react";
import { StepType, StylesObj, useTour } from "@reactour/tour";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

export const IS_TOUR_PASSED = "isTourPassed";

export enum TourViewTarget {
    EXEC_SUMMARY_TAB = "executive-summary-tab",
    COMPENSATION_TAB = "compensation-tab",
    TURNOVER_TAB = "turnover-tab",
    SCHEDULE_TAB = "schedule-tab",
    BENEFITS_TAB = "benefits-tab",
    QUERY_TAB = "query-tab",
    TRENDS_TAB = "trends-tab",
    FAQ_TAB = "faq-tab",
}

// Section-specific completion tracking
export const TOUR_SECTION_COMPLETED = {
    [TourViewTarget.EXEC_SUMMARY_TAB]: "tour_completed_executive_summary",
    [TourViewTarget.COMPENSATION_TAB]: "tour_completed_compensation",
    [TourViewTarget.TURNOVER_TAB]: "tour_completed_turnover",
    [TourViewTarget.SCHEDULE_TAB]: "tour_completed_schedule",
    [TourViewTarget.BENEFITS_TAB]: "tour_completed_benefits",
    [TourViewTarget.QUERY_TAB]: "tour_completed_query",
    [TourViewTarget.TRENDS_TAB]: "tour_completed_trends",
    [TourViewTarget.FAQ_TAB]: "tour_completed_faq",
} as const;

export const resetTour = () => {
    for (const value of Object.values(TOUR_SECTION_COMPLETED)) {
        localStorage.removeItem(value);
    }
};

// Helper functions for section completion
export const isSectionCompleted = (section: TourViewTarget): boolean => {
    return localStorage.getItem(TOUR_SECTION_COMPLETED[section]) === "true";
};

export const setSectionCompleted = (section: TourViewTarget): void => {
    localStorage.setItem(TOUR_SECTION_COMPLETED[section], "true");
};

interface StepContentProps {
    text: string;
    section: TourViewTarget;
    title?: string;
    buttonText?: string;
    children?: ReactNode;
}

export const TourStepContent: React.FC<StepContentProps> = ({
    text,
    title,
    buttonText,
    section,
    children,
}) => {
    const { setIsOpen } = useTour();
    const { t } = useTranslation();
    const skipText = buttonText || t("tour.buttons.skip");

    return (
        <div className="reactour__content">
            {title && (
                <Typography component="div" variant="body1Medium" mb={1}>
                    {title}
                </Typography>
            )}
            <Box position="absolute" right="8px" top="8px" display="flex" justifyContent="flex-end">
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                        setSectionCompleted(section);
                        setIsOpen(false);
                    }}
                >
                    {skipText}
                </Button>
            </Box>
            <Typography component="div">{text}</Typography>
            {children}
        </div>
    );
};

interface FinalStepContentProps {
    text: string;
    title?: string;
    section: TourViewTarget;
    children?: ReactNode;
}

export const TourFinalStepContent: React.FC<FinalStepContentProps> = ({
    text,
    title,
    section,
    children,
}) => {
    const { setIsOpen } = useTour();
    const { t } = useTranslation();

    return (
        <div className="reactour__content">
            {title && (
                <Typography component="div" variant="body1Medium" mb={1}>
                    {title}
                </Typography>
            )}
            <Typography component="div">{text}</Typography>
            {children}
            <Box display="flex" justifyContent="space-between" gap={1}>
                <Button
                    variant="contained"
                    size="small"
                    sx={{ position: "absolute", bottom: 20, right: 20 }}
                    onClick={() => {
                        setSectionCompleted(section);
                        setIsOpen(false);
                    }}
                >
                    {t("tour.buttons.finish")}
                </Button>
            </Box>
        </div>
    );
};

const popoverAtSideBarStyles = { popover: (props: StylesObj) => ({ ...props, left: "25px" }) };

function getTourSteps(t: (key: string) => string): Partial<Record<TourViewTarget, StepType[]>> {
    return {
        [TourViewTarget.EXEC_SUMMARY_TAB]: [
            {
                selector: ".exec-step-1",
                content: (
                    <TourStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step1.title")}
                        text={t("tour.executiveSummary.step1.text")}
                    />
                ),
                styles: popoverAtSideBarStyles,
            },
            {
                selector: ".exec-step-2",
                content: (
                    <TourStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step2.title")}
                        text={t("tour.executiveSummary.step2.text")}
                    />
                ),
            },
            {
                selector: ".exec-step-3",
                content: (
                    <TourStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step3.title")}
                        text={t("tour.executiveSummary.step3.text")}
                    />
                ),
            },
            {
                selector: ".exec-step-4",
                content: (
                    <TourStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step4.title")}
                        text={t("tour.executiveSummary.step4.text")}
                    />
                ),
            },
            {
                selector: ".exec-step-5",
                content: (
                    <TourStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step5.title")}
                        text={t("tour.executiveSummary.step5.text")}
                    >
                        <ul style={{ listStyle: "inside", paddingLeft: 12 }}>
                            <Typography variant="body1" component="li">
                                {t("tour.ranking.thumbsUp")}
                            </Typography>
                            <Typography variant="body1" component="li">
                                {t("tour.ranking.thumbsDown")}
                            </Typography>
                            <Typography variant="body1" component="li">
                                {t("tour.ranking.star")}
                            </Typography>
                        </ul>
                    </TourStepContent>
                ),
            },
            {
                position: "top",
                selector: ".exec-step-6",
                content: (
                    <TourStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step6.title")}
                        text={t("tour.executiveSummary.step6.text")}
                    />
                ),
            },
            {
                position: "top",
                selector: ".exec-step-7",
                styles: popoverAtSideBarStyles,
                content: (
                    <TourStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step7.title")}
                        text={t("tour.executiveSummary.step7.text")}
                    />
                ),
            },
            {
                position: "top",
                selector: ".exec-step-8",
                styles: popoverAtSideBarStyles,
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.EXEC_SUMMARY_TAB}
                        title={t("tour.executiveSummary.step8.title")}
                        text={t("tour.executiveSummary.step8.text")}
                    />
                ),
            },
        ],
        [TourViewTarget.COMPENSATION_TAB]: [
            {
                selector: ".comp-step-1",
                content: (
                    <TourStepContent
                        section={TourViewTarget.COMPENSATION_TAB}
                        title={t("tour.compensation.step1.title")}
                        text={t("tour.compensation.step1.text")}
                    />
                ),
            },
            {
                selector: ".comp-step-2",
                content: (
                    <TourStepContent
                        section={TourViewTarget.COMPENSATION_TAB}
                        title={t("tour.compensation.step2.title")}
                        text={t("tour.compensation.step2.text")}
                    />
                ),
            },
            {
                selector: ".comp-step-3",
                content: (
                    <TourStepContent
                        section={TourViewTarget.COMPENSATION_TAB}
                        title={t("tour.compensation.step3.title")}
                        text={t("tour.compensation.step3.text")}
                    />
                ),
            },
            {
                selector: ".comp-step-4",
                content: (
                    <TourStepContent
                        section={TourViewTarget.COMPENSATION_TAB}
                        title={t("tour.compensation.step4.title")}
                        text={t("tour.compensation.step4.text")}
                    />
                ),
            },
            {
                selector: ".comp-step-5",
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.COMPENSATION_TAB}
                        title={t("tour.compensation.step5.title")}
                        text={t("tour.compensation.step5.text")}
                    />
                ),
            },
        ],
        [TourViewTarget.TURNOVER_TAB]: [
            {
                selector: ".turnover-step-1",
                content: (
                    <TourStepContent
                        section={TourViewTarget.TURNOVER_TAB}
                        title={t("tour.turnover.step1.title")}
                        text={t("tour.turnover.step1.text")}
                    />
                ),
            },
            {
                selector: ".turnover-step-2",
                content: (
                    <TourStepContent
                        section={TourViewTarget.TURNOVER_TAB}
                        title={t("tour.turnover.step2.title")}
                        text={t("tour.turnover.step2.text")}
                    />
                ),
            },
            {
                selector: ".turnover-step-3",
                content: (
                    <TourStepContent
                        section={TourViewTarget.TURNOVER_TAB}
                        title={t("tour.turnover.step3.title")}
                        text={t("tour.turnover.step3.text")}
                    />
                ),
            },
            {
                selector: ".turnover-step-4",
                content: (
                    <TourStepContent
                        section={TourViewTarget.TURNOVER_TAB}
                        title={t("tour.turnover.step4.title")}
                        text={t("tour.turnover.step4.text")}
                    />
                ),
            },
            {
                selector: ".turnover-step-5",
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.TURNOVER_TAB}
                        title={t("tour.turnover.step5.title")}
                        text={t("tour.turnover.step5.text")}
                    />
                ),
            },
        ],
        [TourViewTarget.SCHEDULE_TAB]: [
            {
                selector: ".schedule-step-1",
                content: (
                    <TourStepContent
                        section={TourViewTarget.SCHEDULE_TAB}
                        title={t("tour.schedule.step1.title")}
                        text={t("tour.schedule.step1.text")}
                    />
                ),
            },
            {
                selector: ".schedule-step-2",
                content: (
                    <TourStepContent
                        section={TourViewTarget.SCHEDULE_TAB}
                        title={t("tour.schedule.step2.title")}
                        text={t("tour.schedule.step2.text")}
                    />
                ),
            },
            {
                selector: ".schedule-step-3",
                content: (
                    <TourStepContent
                        section={TourViewTarget.SCHEDULE_TAB}
                        title={t("tour.schedule.step3.title")}
                        text={t("tour.schedule.step3.text")}
                    />
                ),
            },
            {
                selector: ".schedule-step-4",
                content: (
                    <TourStepContent
                        section={TourViewTarget.SCHEDULE_TAB}
                        title={t("tour.schedule.step4.title")}
                        text={t("tour.schedule.step4.text")}
                    />
                ),
            },
            {
                selector: ".schedule-step-5",
                content: (
                    <TourStepContent
                        section={TourViewTarget.SCHEDULE_TAB}
                        title={t("tour.schedule.step5.title")}
                        text={t("tour.schedule.step5.text")}
                    />
                ),
            },
            {
                selector: ".schedule-step-6",
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.SCHEDULE_TAB}
                        title={t("tour.schedule.step6.title")}
                        text={t("tour.schedule.step6.text")}
                    />
                ),
            },
        ],
        [TourViewTarget.BENEFITS_TAB]: [
            {
                selector: ".benefits-step-1",
                content: (
                    <TourStepContent
                        section={TourViewTarget.BENEFITS_TAB}
                        title={t("tour.benefits.step1.title")}
                        text={t("tour.benefits.step1.text")}
                    />
                ),
            },
            {
                selector: ".benefits-step-2",
                content: (
                    <TourStepContent
                        section={TourViewTarget.BENEFITS_TAB}
                        title={t("tour.benefits.step2.title")}
                        text={t("tour.benefits.step2.text")}
                    />
                ),
            },
            {
                selector: ".benefits-step-3",
                content: (
                    <TourStepContent
                        section={TourViewTarget.BENEFITS_TAB}
                        title={t("tour.benefits.step3.title")}
                        text={t("tour.benefits.step3.text")}
                    />
                ),
            },
            {
                selector: ".benefits-step-4",
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.BENEFITS_TAB}
                        title={t("tour.benefits.step4.title")}
                        text={t("tour.benefits.step4.text")}
                    />
                ),
            },
        ],
        [TourViewTarget.QUERY_TAB]: [
            {
                selector: ".query-step-1",
                content: (
                    <TourStepContent
                        section={TourViewTarget.QUERY_TAB}
                        title={t("tour.query.step1.title")}
                        text={t("tour.query.step1.text")}
                    />
                ),
            },
            {
                selector: ".query-step-2",
                content: (
                    <TourStepContent
                        section={TourViewTarget.QUERY_TAB}
                        title={t("tour.query.step2.title")}
                        text={t("tour.query.step2.text")}
                    />
                ),
            },
            {
                selector: ".query-step-3",
                content: (
                    <TourStepContent
                        section={TourViewTarget.QUERY_TAB}
                        title={t("tour.query.step3.title")}
                        text={t("tour.query.step3.text")}
                    />
                ),
            },
            {
                selector: ".query-step-4",
                content: (
                    <TourStepContent
                        section={TourViewTarget.QUERY_TAB}
                        title={t("tour.query.step4.title")}
                        text={t("tour.query.step4.text")}
                    />
                ),
            },
            {
                selector: ".query-step-5",
                content: (
                    <TourStepContent
                        section={TourViewTarget.QUERY_TAB}
                        title={t("tour.query.step5.title")}
                        text={t("tour.query.step5.text")}
                    />
                ),
            },
            {
                selector: ".query-step-6",
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.QUERY_TAB}
                        title={t("tour.query.step6.title")}
                        text={t("tour.query.step6.text")}
                    />
                ),
            },
        ],
        [TourViewTarget.TRENDS_TAB]: [
            {
                selector: ".trends-step-1",
                styles: popoverAtSideBarStyles,
                content: (
                    <TourStepContent
                        section={TourViewTarget.TRENDS_TAB}
                        title={t("tour.trends.step1.title")}
                        text={t("tour.trends.step1.text")}
                    />
                ),
            },
            {
                selector: ".trends-step-2",
                content: (
                    <TourStepContent
                        section={TourViewTarget.TRENDS_TAB}
                        title={t("tour.trends.step2.title")}
                        text={t("tour.trends.step2.text")}
                    />
                ),
            },
            {
                selector: ".trends-step-3",
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.TRENDS_TAB}
                        title={t("tour.trends.step3.title")}
                        text={t("tour.trends.step3.text")}
                    />
                ),
            },
        ],
        [TourViewTarget.FAQ_TAB]: [
            {
                selector: ".faq-step-1",
                styles: popoverAtSideBarStyles,
                content: (
                    <TourStepContent
                        section={TourViewTarget.FAQ_TAB}
                        title={t("tour.faq.step1.title")}
                        text={t("tour.faq.step1.text")}
                    />
                ),
            },
            {
                selector: ".faq-step-2",
                content: (
                    <TourFinalStepContent
                        section={TourViewTarget.FAQ_TAB}
                        title={t("tour.faq.step2.title")}
                        text={t("tour.faq.step2.text")}
                    />
                ),
            },
        ],
    };
}

export const useTourSteps = (viewTarget?: TourViewTarget) => {
    const { t } = useTranslation();
    const isGloballySkipped = !!localStorage.getItem(IS_TOUR_PASSED);
    const isSectionComplete = viewTarget ? isSectionCompleted(viewTarget) : false;
    const shouldShowTour = !isGloballySkipped && !isSectionComplete;

    const { setIsOpen, setCurrentStep, setSteps } = useTour();

    useEffect(() => {
        if (shouldShowTour && viewTarget) {
            const tourStepsFull = getTourSteps(t);
            const steps = tourStepsFull[viewTarget] || [];

            if (steps.length > 0 && setSteps) {
                setSteps(steps);
                setCurrentStep(0);
                setIsOpen(true);
            }
        }
    }, [shouldShowTour, viewTarget, setSteps, setCurrentStep, setIsOpen, t]);
};
